import { useCallback, useMemo, useState } from "react";
import { useDataProvider, useGetIdentity, useNotify } from "react-admin";

import { resolveCsvRows } from "./resolveCsvRows";
import { serializeCsv, withUtf8Bom } from "./serializeCsv";
import { CsvExportContext, CsvExportDefinition } from "./types";

export interface UseTableCsvExportOptions<T> {
  definition: CsvExportDefinition<T>;
  /** Records from the currently visible react-admin page, in display order. */
  records: readonly T[];
  context?: CsvExportContext;
  /** A caller may further restrict display, but cannot grant export access. */
  isSuperAdmin?: boolean;
  concurrency?: number;
}

export interface TableCsvExportState {
  isAuthorized: boolean;
  isLoading: boolean;
  downloadCsv: () => Promise<void>;
  copyCsv: () => Promise<void>;
}

const isIdentitySuperAdmin = (identity: unknown): boolean =>
  isRecord(identity) &&
  isRecord(identity.defaultMembership) &&
  isRecord(identity.defaultMembership.role) &&
  identity.defaultMembership.role.superAdmin === true;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object";

const filenameFor = (stem: string, date = new Date()): string => {
  const localDate = [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0")
  ].join("-");
  const safeStem = stem
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `${safeStem || "export"}-${localDate}.csv`;
};

export const useTableCsvExport = <T extends { id?: string | number }>(
  options: UseTableCsvExportOptions<T>
): TableCsvExportState => {
  const { definition, records, context, isSuperAdmin, concurrency } = options;
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { data: identity } = useGetIdentity();
  const [isLoading, setIsLoading] = useState(false);
  const isAuthorized = useMemo(
    () =>
      isIdentitySuperAdmin(identity) &&
      (isSuperAdmin === undefined || isSuperAdmin === true),
    [identity, isSuperAdmin]
  );

  const buildCsv = useCallback(async (): Promise<string> => {
    const resolved = await resolveCsvRows(
      definition,
      Array.from(records),
      { ...context, dataProvider },
      { concurrency }
    );
    return serializeCsv(resolved.headers, resolved.rows);
  }, [concurrency, context, dataProvider, definition, records]);

  const run = useCallback(
    async (
      action: (csv: string) => Promise<void> | void,
      successMessage: string
    ) => {
      if (!isAuthorized || isLoading) {
        return;
      }
      setIsLoading(true);
      try {
        const csv = await buildCsv();
        await action(csv);
        notify(successMessage, { type: "success" });
      } catch (error) {
        notify("Unable to export CSV. Please try again.", { type: "warning" });
      } finally {
        setIsLoading(false);
      }
    },
    [buildCsv, isAuthorized, isLoading, notify]
  );

  const downloadCsv = useCallback(
    () =>
      run((csv) => {
        const url = URL.createObjectURL(
          new Blob([withUtf8Bom(csv)], { type: "text/csv;charset=utf-8" })
        );
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = filenameFor(definition.filenameStem);
        anchor.style.display = "none";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
      }, "CSV download started"),
    [definition.filenameStem, run]
  );

  const copyCsv = useCallback(
    () =>
      run(async (csv) => {
        if (!navigator.clipboard || !navigator.clipboard.writeText) {
          throw new Error("Clipboard is unavailable");
        }
        await navigator.clipboard.writeText(csv);
      }, "CSV copied to clipboard"),
    [run]
  );

  return { isAuthorized, isLoading, downloadCsv, copyCsv };
};

export { filenameFor };
